import config from "../../config/domain";
import GlobalStore from "../store/globalStore";
import { localStorageGetItem } from "./globalService";
import requestMapper from "./mapper/serviceMapper";
import servicesList from "./serviceList";
import { trackApiError } from "./apiErrorTracker";
import { logApiCall } from "./apiServices";
import { handleSessionExpired, isSessionExpired, parseResponseBody } from "./sessionExpiry";
import { prepareSecureRequest, decryptSecureResponse } from "../../secureApi/secureEncryption";

interface callReq {
  url: string;
  body: {};
  method?: string;
  apiBaseUrl?: string;
  tempToken?: string;
  serviceName?: string;
  isNewUrl?: boolean;
  encrypted?: boolean;
}

export const xhrApiService = async (
  req: callReq,
  callback: CallableFunction,
  onProgress?: Function,
  method?: string,
  apiBaseUrl?: string,
  serviceName?: string,
  tempToken?: string,
  isNewUrl?: boolean
) => {
  let xhr = new XMLHttpRequest();

  // track upload progress
  xhr.upload.onprogress = function (event) {
    onProgress && onProgress(event);
  };

  xhr.onerror = function () {
    logApiCall({ method: httpMethod, url: plainUrl, payload: req.body, error: "upload request failed" });
    trackApiError({
      url: req.url,
      serviceName,
      errorType: "network",
      message: "upload request failed",
    });
  };

  // track completion: both successful or not
  xhr.onloadend = async function (res) {
    resolveResult(res);
    // if (serviceName && serviceName !== "") {
    //   let result = await requestMapper(this.responseText, serviceName);
    //   callback(result);
    // } else {
    //   callback(res);
    // }
    // if (xhr.status === 200) {
    //   // callback(JSON.parse(this.responseText));
    // } else {
    //   callback(undefined);
    // }
  };

  const resolveResult = async (res: any) => {
    if (res.currentTarget) {
      if (res.currentTarget.responseText) {
        let parsed: any;
        try {
          parsed = JSON.parse(res.currentTarget.responseText);
        } catch (e) {
          logApiCall({ method: httpMethod, url: plainUrl, status: xhr.status, payload: req.body, response: res.currentTarget.responseText, error: e });
          trackApiError({
            url: req.url,
            serviceName,
            httpStatus: xhr.status,
            errorType: "network",
            message: "invalid upload response",
          });
          callback(res.currentTarget);
          return;
        }
        logApiCall({ method: httpMethod, url: plainUrl, status: xhr.status, payload: req.body, response: parsed });

        trackApiError({
          url: req.url,
          serviceName,
          httpStatus: xhr.status,
          response: parsed,
        });

        if (isSessionExpired(xhr.status, parsed) && handleSessionExpired(parsed)) return;

        let result = await requestMapper(
          parsed,
          servicesList.uploadDocumentToS3
        );
        if (result) {
          callback(result);
        }
      } else {
        logApiCall({ method: httpMethod, url: plainUrl, status: xhr.status, payload: req.body, error: "empty upload response" });
        trackApiError({
          url: req.url,
          serviceName,
          httpStatus: xhr.status,
          errorType: "network",
          message: "empty upload response",
        });
        callback(res.currentTarget);
      }
    }
  };

  const httpMethod = method ? method : "POST";
  const plainUrl = `${config.diyNewApiUrl}${req.url}`;
  let clientPartialDetails = JSON.parse(
    localStorageGetItem("clientPartialDetails_ao") || "{}"
  );

  if (req.encrypted) {
    const fullUrl = `${config.diyNewApiUrl}v1/secured/${req.url}`;
    let secure: Awaited<ReturnType<typeof prepareSecureRequest>>;
    try {
      secure = await prepareSecureRequest({
        method: httpMethod,
        url: fullUrl,
        serverPublicKey: config.secureApiPublicKey,
        hmacSecret: config.secureApiHmacSecret,
        plainBody: req.body,
      });
    } catch (err) {
      logApiCall({ method: httpMethod, url: fullUrl, payload: req.body, error: err });
      console.error("[xhrApiService] Secure request preparation failed:", req.url, err);
      trackApiError({
        url: req.url,
        serviceName,
        errorType: "network",
        message: (err as Error)?.message || String(err),
      });
      callback({ error: "", errMsg: err });
      return;
    }

    // Response must be decrypted before mapping, so replace the plain handler.
    xhr.onloadend = async function () {
      const encryptedKey = xhr.getResponseHeader("X-Response-Encrypted-Key");
      // Session expiry comes back as a plain (unencrypted) 401 body.
      const plainBody = encryptedKey ? undefined : parseResponseBody(xhr.responseText);
      if (!encryptedKey && isSessionExpired(xhr.status, plainBody)) {
        logApiCall({ method: httpMethod, url: fullUrl, status: xhr.status, payload: req.body, response: plainBody ?? xhr.responseText });
        trackApiError({ url: req.url, serviceName, httpStatus: xhr.status, response: plainBody });
        if (handleSessionExpired(plainBody)) return;
      }
      if (!encryptedKey) {
        logApiCall({
          method: httpMethod,
          url: fullUrl,
          status: xhr.status,
          payload: req.body,
          response: xhr.responseText,
          error: "SecureApi: X-Response-Encrypted-Key header not readable.",
        });
        console.error("[xhrApiService] Secure API response not encrypted:", fullUrl, xhr.status, xhr.responseText);
        trackApiError({
          url: req.url,
          serviceName,
          httpStatus: xhr.status,
          errorType: "network",
          message: "SecureApi: X-Response-Encrypted-Key header not readable.",
        });
        callback(xhr);
        return;
      }
      try {
        const decryptedData = await decryptSecureResponse<any>({
          response: new Response(xhr.responseText, {
            headers: { "X-Response-Encrypted-Key": encryptedKey },
          }),
          clientKeyPair: secure.clientKeyPair,
          expectedNonce: secure.nonce,
        });
        logApiCall({ method: httpMethod, url: fullUrl, status: xhr.status, payload: req.body, response: decryptedData });
        trackApiError({
          url: req.url,
          serviceName,
          httpStatus: xhr.status,
          response: decryptedData,
        });
        if (isSessionExpired(xhr.status, decryptedData) && handleSessionExpired(decryptedData)) return;
        const result = await requestMapper(
          decryptedData,
          servicesList.uploadDocumentToS3
        );
        if (result) {
          callback(result);
        }
      } catch (err) {
        logApiCall({ method: httpMethod, url: fullUrl, status: xhr.status, payload: req.body, error: err });
        console.error("[xhrApiService] Secure API decrypt failed:", fullUrl, err);
        trackApiError({
          url: req.url,
          serviceName,
          httpStatus: xhr.status,
          errorType: "network",
          message: (err as Error)?.message || String(err),
        });
        callback(xhr);
      }
    };

    xhr.open(httpMethod, fullUrl);
    xhr.setRequestHeader("Content-Type", "application/json");
    const authToken = GlobalStore.token || clientPartialDetails.token;
    if (authToken) {
      xhr.setRequestHeader("Authorization", `Bearer ${authToken}`);
    }
    Object.entries(secure.headers).forEach(([key, value]) =>
      xhr.setRequestHeader(key, value)
    );
    xhr.send(secure.body ?? null);
    return;
  }

  xhr.open(httpMethod, plainUrl);
  xhr.setRequestHeader("Content-Type", "application/json");
  xhr.setRequestHeader("api-key", config.apiKey);
  if (GlobalStore.token !== "") {
    xhr.setRequestHeader("Authorization", `Bearer ${GlobalStore.token}`);
  }

  if (!GlobalStore.token && clientPartialDetails.token) {
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${clientPartialDetails.token}`
    );
  }
  xhr.send(JSON.stringify(req.body));
};
